import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ChevronRight,
  LogOut,
  Moon,
  Pencil,
  Settings,
  Sun,
} from "lucide-react";
import SidebarExtension from "./SidebarExtension";
import { useDispatch, useSelector } from "react-redux";
import { selectTheme } from "@/store/selectors/theme";
import { setTheme as setThemeAction, ThemeMode } from "@/store/features/theme";
import {
  useAuthenticationContext,
  useCurrentWorkspace,
} from "@/store/authContext/authContext";
import { AppDispatch } from "@/store";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PORTAL_URL } from "@/constants/api";
import type { ParitaWorkspace } from "@/common/user";

const MiniSidebarAccountSection: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const currentTheme = useSelector(selectTheme);
  const { state } = useAuthenticationContext();
  const { userData: user, handleLogout } = state;
  const selectedWorkspace = useCurrentWorkspace();
  const workspaces: ParitaWorkspace[] = user?.workspaces || [];

  const [theme, setTheme] = useState(() => {
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches &&
      !currentTheme
    ) {
      return "dark";
    }
    return currentTheme || "light";
  });

  const handleWorkspaceChange = async (value: string) => {
    // Workspace change is handled on the portal side
    window.location.href = `${PORTAL_URL}workspace/select?workspace=${value}`;
  };

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
    dispatch(setThemeAction(newTheme as ThemeMode));
  };

  const isCurrentWorkspaceAdmin = selectedWorkspace?.is_admin || false;

  const accountContent = (
    <div className="space-y-1">
      {workspaces.length > 1 && (
        <div className="px-4 py-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Change Workspace
          </label>
          <Select
            value={selectedWorkspace.uuid}
            onValueChange={handleWorkspaceChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select workspace" />
            </SelectTrigger>
            <SelectContent>
              {workspaces.map((workspace: ParitaWorkspace) => (
                <SelectItem key={workspace.uuid} value={workspace.uuid}>
                  {workspace.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <button
        onClick={toggleTheme}
        className="w-full text-left px-4 py-2 text-sm text-sea-500 hover:text-sea-600 flex items-center transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
      >
        {theme === "dark" ? (
          <Sun size={16} className="mr-2" />
        ) : (
          <Moon size={16} className="mr-2" />
        )}
        {theme === "dark" ? "Light Mode" : "Dark Mode"}
      </button>
      <a
        href="https://mydashboards.parita.com/user/me"
        className="block w-full text-left px-4 py-2 text-sm text-sea-500 hover:text-sea-600 flex items-center transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
      >
        <Pencil size={16} className="mr-2" />
        Edit Profile
      </a>
      {isCurrentWorkspaceAdmin && (
        <a
          href="https://mydashboards.parita.com/workspace/admin/users"
          className="block w-full text-left px-4 py-2 text-sm text-sea-500 hover:text-sea-600 flex items-center transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
        >
          <Settings size={16} className="mr-2" />
          Admin
        </a>
      )}
      <button
        onClick={handleLogout}
        className="w-full text-left px-4 py-2 text-sm text-sea-500 hover:text-sea-600 flex items-center transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
      >
        <LogOut size={16} className="mr-2" />
        Log out
      </button>
    </div>
  );

  return (
    <>
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        className="w-12 h-12 rounded-full relative group text-muted-foreground hover:text-foreground dark:hover:text-black hover:bg-sky-100"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setIsOpen(true)}
      >
        <div className="flex items-center">
          <img
            src={
              selectedWorkspace?.company_logo || "/web-app-manifest-192x192.png"
            }
            alt="Company Logo"
            className="w-8 h-8"
          />
          {isHovered && (
            <ChevronRight
              size={14}
              className="absolute right-0 text-gray-400"
            />
          )}
        </div>
      </Button>

      <SidebarExtension
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={selectedWorkspace?.name || "Settings"}
        triggerRef={buttonRef}
      >
        {accountContent}
      </SidebarExtension>
    </>
  );
};

export default MiniSidebarAccountSection;
